import { useState, useEffect, useCallback } from "react";
import type { CurrencyCode } from "@/hooks/useCurrency";
import { products } from "@/mocks/products";

export interface CartItem {
  familyName: string;
  code: string;
  spec: string;
  price: number;
  quantity: number;
  isGift?: boolean;
}

const CART_KEY = "peptide_cart";
// Excel catalog identifiers changed; preserve pre-update baskets by mapping old SKU -> new SKU.
const LEGACY_CODE_MAP: Record<string, string> = {
  "AOD10": "10AD",
  "AOD5": "5AD",
  "ARA10": "RA10",
  "ARA16": "RA16",
  "BA10": "BAC10(BA10)",
  "BA3": "BAC3(BA3)",
  "BBG70": "GLOW70",
  "IPA10": "IP10",
  "IPA5": "IP5",
  "KLOW80": "KLOW80(BBKG80)",
  "KPV10": "KP10",
  "KPV5": "KP5",
  "ML10": "MT-2",
  "P4110": "P41",
  "SSXK": "XS20",
  "SX10": "XA10",
  "SX5": "XA5",
  "TB10": "BT10",
  "TB5": "BT5"
};
export const SHIPPING_COST_USD = 55;
export const SHIPPING_COST_AUD = 80;
export const FREE_SHIPPING_THRESHOLD_USD = 1000;
export const FREE_SHIPPING_THRESHOLD_AUD = 1200;

export function getShippingCost(currency: CurrencyCode): number {
  return currency === 'AUD' ? SHIPPING_COST_AUD : SHIPPING_COST_USD;
}

export function getFreeShippingThreshold(currency: CurrencyCode): number {
  return currency === 'AUD' ? FREE_SHIPPING_THRESHOLD_AUD : FREE_SHIPPING_THRESHOLD_USD;
}

function calcShipping(subtotal: number, currency: CurrencyCode): number {
  if (subtotal >= getFreeShippingThreshold(currency)) return 0;
  return getShippingCost(currency);
}

// Always reprice saved cart entries against the current catalog on page reload.
// This prevents prices stored by an older deployment from being reused.
function loadCart(currency: CurrencyCode): CartItem[] {
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return [];
    const saved: CartItem[] = JSON.parse(raw);
    if (!Array.isArray(saved)) return [];
    const currentCatalog = new Map(
      products.flatMap((family) => family.variants.map((variant) => [
        variant.code,
        { familyName: family.name, ...variant },
      ] as const))
    );
    const normalized = new Map<string, CartItem>();
    for (const item of saved) {
      if (!item || typeof item.code !== 'string') continue;
      const code = LEGACY_CODE_MAP[item.code] ?? item.code;
      const catalogItem = currentCatalog.get(code);
      if (!catalogItem) continue; // product discontinued, not charged at an obsolete price
      const quantity = Number.isFinite(item.quantity) ? Math.floor(item.quantity) : 0;
      if (quantity <= 0) continue;
      const isGift = Boolean(item.isGift);
      const key = `${code}|${isGift ? 'gift' : 'normal'}`;
      const prior = normalized.get(key);
      normalized.set(key, {
        ...item,
        code,
        familyName: catalogItem.familyName,
        spec: catalogItem.spec,
        quantity: (prior?.quantity ?? 0) + quantity,
        price: isGift ? 0 : (currency === 'AUD' ? catalogItem.audPrice : catalogItem.price),
      });
    }
    return [...normalized.values()];
  } catch {
    return [];
  }
}

function saveCart(items: CartItem[]) {
  localStorage.setItem(CART_KEY, JSON.stringify(items));
}

export function useCart(currency: CurrencyCode) {
  const [items, setItems] = useState<CartItem[]>(() => loadCart(currency));
  const [discountRate, setDiscountRate] = useState(0);

  useEffect(() => {
    saveCart(items);
  }, [items]);

  const addItem = useCallback((item: Omit<CartItem, "quantity" | "isGift">) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.code === item.code && !i.isGift);
      if (existing) {
        return prev.map((i) =>
          i.code === item.code && !i.isGift ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, { ...item, quantity: 1 }];
    });
  }, []);

  const addGiftItem = useCallback((item: Omit<CartItem, "quantity" | "isGift" | "price">) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.code === item.code && i.isGift);
      if (existing) {
        return prev.map((i) =>
          i.code === item.code && i.isGift ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, { ...item, price: 0, quantity: 1, isGift: true }];
    });
  }, []);

  const removeItem = useCallback((code: string) => {
    setItems((prev) => prev.filter((i) => i.code !== code));
  }, []);

  const updateQuantity = useCallback((code: string, quantity: number, isGift?: boolean) => {
    if (quantity <= 0) {
      setItems((prev) => prev.filter((i) => !(i.code === code && i.isGift === isGift)));
      return;
    }
    setItems((prev) =>
      prev.map((i) => (i.code === code && i.isGift === isGift ? { ...i, quantity } : i))
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const totalCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const discount = Math.round(subtotal * discountRate);
  const discountedSubtotal = subtotal - discount;
  const shipping = calcShipping(subtotal, currency);
  const totalPrice = discountedSubtotal + shipping;

  const getOrderText = useCallback(() => {
    const shippingCost = getShippingCost(currency);
    const freeThreshold = getFreeShippingThreshold(currency);
    const symbol = currency === 'AUD' ? 'A$' : '$';
    const lines = items.map(
      (i) => `${i.familyName} | ${i.code} | ${i.spec} | ${i.isGift ? "FREE GIFT" : `${symbol}${i.price}`} × ${i.quantity}`
    );
    lines.push(`\nSubtotal: ${symbol}${Math.round(subtotal)}`);
    lines.push(`Shipping: ${shipping === 0 ? `FREE (orders over ${symbol}${freeThreshold})` : `${symbol}${Math.round(shipping)}`}`);
    lines.push(`Total: ${symbol}${Math.round(totalPrice)}`);
    return lines.join("\n");
  }, [items, subtotal, shipping, totalPrice, currency]);

  return {
    items,
    addItem,
    addGiftItem,
    removeItem,
    updateQuantity,
    clearCart,
    totalCount,
    subtotal,
    discount,
    discountRate,
    setDiscountRate,
    discountedSubtotal,
    shipping,
    totalPrice,
    getOrderText,
  };
}